import React, { useMemo } from "react"
import cn from "classnames"
import { useSelect } from "downshift"
import { Help, ErrorMessage, RequiredMark, describedBy } from "./Field"
import styles from "../../styles/scss/components/form/formSystem.module.scss"

// Select-only combobox styled to the form-system mockup. Downshift's
// useSelect supplies the WAI-ARIA 1.2 pattern: the trigger is
// role="combobox" and keeps focus, the list is role="listbox", the active
// option is exposed with aria-activedescendant, and keyboard support covers
// Up/Down, Home/End, Enter/Space, Escape, Tab and type-ahead.
//
// Same props as the native <select> it replaced. onChange receives an
// event-like { target: { name, id, value } }, so Formik's handleChange (via
// bind()) and handlers reading e.target.value work unchanged.
//
// `options` accepts the shapes the legacy Select took:
//   { value, text } | { value, label } | "plain string"
export const toOption = o =>
    typeof o === "object" && o !== null
        ? { value: String(o.value), label: o.label || o.text || String(o.value) }
        : { value: String(o), label: String(o) }

const Chevron = () => (
    <svg viewBox="0 0 12 8" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
        <path d="M1 1.5 6 6.5l5-5" />
    </svg>
)

const Select = ({
    name,
    id = name,
    label,
    options = [],
    placeholder = "Select",
    required,
    help,
    error,
    className,
    value,
    onChange,
    onBlur,
    disabled,
}) => {
    // Downshift announces "X has been selected" whenever `items` or
    // `selectedItem` change identity, so keep both stable across re-renders
    // (every keystroke elsewhere in the form re-renders this) and only rebuild
    // them when the option list or the value actually changes
    const normalized = options.map(toOption)
    const itemsKey = normalized.map(o => `${o.value}\u0000${o.label}`).join("\u0001")
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const items = useMemo(() => normalized, [itemsKey])
    const current = value === null || value === undefined ? "" : String(value)
    const selectedItem = useMemo(() => items.find(o => o.value === current) || null, [items, current])

    const { isOpen, highlightedIndex, getLabelProps, getToggleButtonProps, getMenuProps, getItemProps } =
        useSelect({
            items,
            selectedItem,
            itemToString: item => (item ? item.label : ""),
            // The trigger's id is the field id, so labels, the error summary
            // and aria-describedby all point at the focusable element
            toggleButtonId: id,
            labelId: `${id}-label`,
            menuId: `${id}-listbox`,
            getItemId: index => `${id}-option-${index}`,
            onSelectedItemChange: ({ selectedItem: item }) => {
                if (onChange) onChange({ persist() {}, target: { name, id, type: "select-one", value: item ? item.value : "" } })
            },
        })

    const triggerClass = cn(styles.combo, { [styles.comboPlaceholder]: !selectedItem })
    const aria = {
        "aria-required": required || undefined,
        "aria-invalid": error ? "true" : undefined,
        "aria-describedby": describedBy(id, { help, error }),
    }

    // Downshift drops its click/key handlers when `disabled` is passed; the
    // attribute itself isn't valid on a div, so swap it for aria-disabled
    const { disabled: _disabled, ...triggerProps } = getToggleButtonProps({
        ...aria,
        disabled,
        "aria-disabled": disabled || undefined,
        tabIndex: disabled ? -1 : 0,
        onBlur: () => onBlur && onBlur({ persist() {}, target: { name, id } }),
    })

    return (
        <div className={cn(styles.field, className)}>
            {/* A <label for> can't focus a div, so do it by hand, like a native select */}
            <label
                className={styles.label}
                {...getLabelProps({
                    onClick: () => {
                        const trigger = !disabled && document.getElementById(id)
                        if (trigger) trigger.focus()
                    },
                })}
            >
                {label}
                {required && <RequiredMark />}
            </label>
            <div className={styles.comboWrap}>
                <div className={cn(triggerClass, { [styles.comboDisabled]: disabled })} {...triggerProps}>
                    <span>{selectedItem ? selectedItem.label : placeholder}</span>
                    <Chevron />
                </div>
                {/* Downshift needs the listbox in the DOM while closed */}
                <ul className={cn(styles.listbox, { [styles.listboxOpen]: isOpen })} {...getMenuProps()}>
                    {isOpen &&
                        items.map((item, index) => (
                            <li
                                key={item.value}
                                className={cn(styles.option, {
                                    [styles.optionActive]: highlightedIndex === index,
                                    [styles.optionSelected]: selectedItem && selectedItem.value === item.value,
                                })}
                                {...getItemProps({ item, index })}
                            >
                                {item.label}
                            </li>
                        ))}
                </ul>
            </div>
            <Help id={id}>{help}</Help>
            <ErrorMessage id={id}>{error}</ErrorMessage>
        </div>
    )
}

export default Select
